/**
 * Copyright (C) 2018–2020 HERE Global B.V. and its affiliate(s).
 * All rights reserved.
 *
 * This software and other materials contain proprietary information
 * controlled by HERE and are protected by applicable copyright legislation.
 * Any use and utilization of this software and other materials and
 * disclosure to any third parties is conditional upon having a separate
 * agreement with HERE for the access, use, utilization or disclosure of this
 * software. In the absence of such agreement, the use of the software is not
 * allowed.
 */

import LuiBase from './lui-base'

export default class LuiInputBase extends LuiBase {
  static get clickTriggers() {
    return []
  }

  static get clickEvent() {
    return 'click'
  }

  static get hasClickTriggers() {
    return this.clickTriggers.length !== 0
  }

  init() {
    this._makeFocusable()
    super.init()
  }

  onConnected() {
    if (this.disabled) return
    this._onFocusBound = this._onFocusBound || this._onFocus.bind(this)
    this._onBlurBound = this._onBlurBound || this._onBlur.bind(this)
    this.eventListenerElement.addEventListener('focus', this._onFocusBound)
    this.eventListenerElement.addEventListener('blur', this._onBlurBound)
  }

  onDisconnected() {
    if (this.disabled) return
    this.removeEventListener('focus', this._onFocus)
    this.removeEventListener('blur', this._onBlur)
  }

  disabledChanged() {
    if (this.disabled) {
      this.removeAttribute('tabindex')
      this.eventListenerElement.removeEventListener('focus', this._onFocusBound)
      this.eventListenerElement.removeEventListener('blur', this._onBlurBound)
    } else {
      this._makeFocusable()
      this.eventListenerElement.addEventListener('focus', this._onFocusBound)
      this.eventListenerElement.addEventListener('blur', this._onBlurBound)
    }
  }

  _makeFocusable() {
    if (!this.disabled &&
      (this.field === this || window.getComputedStyle(this.field).display === 'none')) {
      this.setAttribute('tabindex', 0)
    }
  }

  _onFocus() {
    if (this.constructor.hasClickTriggers) {
      this._onKeyDownBound = this._onKeyDownBound || this._onKeyDown.bind(this)
      this.eventListenerElement.addEventListener('keydown', this._onKeyDownBound)
    }
  }

  _onBlur() {
    if (this.constructor.hasClickTriggers && this._onKeyDownBound) {
      this.eventListenerElement.addEventListener('keydown', this._onKeyDownBound)
    }
  }

  _onKeyDown(event) {
    const clickTriggers = this.constructor.clickTriggers
    // ' ' for modern browsers, 'space bar' for IE:
    const spaceKey = ' |space bar'
    const regexp = new RegExp(clickTriggers.join('|').replace('space', spaceKey), 'i')
    if (event.key.match(regexp)) {
      event.preventDefault()
      event.stopPropagation()
      this._ignoreChange = true

      if (this.constructor.clickEvent === 'click') {
        // .click() is more reliable than creating a MouseEvent/Event, for example for Rails UJS
        this.field.click()
      } else {
        const clickEvent = new window.MouseEvent(this.constructor.clickEvent, { bubbles: true })
        this.field.dispatchEvent(clickEvent)
      }

      this._ignoreChange = false
    }
  }

  _getField() {
    return this
  }

  get field() {
    this._field = this._field || this._getField()
    return this._field
  }

  _getEventListenerElement() {
    let element = this.field
    if (element.querySelector(this.constructor.is) !== this) {
      element = this
    }
    return element
  }

  get eventListenerElement() {
    this._eventListenerElement = this._eventListenerElement || this._getEventListenerElement()
    return this._eventListenerElement
  }

  get disabled() {
    return (this.field !== this && this.field.disabled) || this.hasAttribute('disabled')
  }

  set disabled(value) {
    if (value) {
      this.setAttribute('disabled', '')
    } else {
      this.removeAttribute('disabled')
    }
  }
}



//////////////////
// WEBPACK FOOTER
// ./packages/lui-base/lui-input-base.js
// module id = 5
// module chunks = 0 1 2 3 4 5 6 7 8 9 10 11 12 13 14 15 16 17 18 20 21 22 23 24 25 26 27 28 29 30 31 32 33 34 35 36 37 38